'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { computeEnlargedSize } from '@/lib/browser-image-effects-tools';

// Safety cap so a huge target does not exhaust canvas memory.
const MAX_PIXELS = 40_000_000; // ~40 MP

export default function BrowserImageEnlarger() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [mode, setMode] = useState<'scale' | 'dimensions'>('scale');
  const [scale, setScale] = useState(2);
  const [targetWidth, setTargetWidth] = useState(0);
  const [targetHeight, setTargetHeight] = useState(0);
  const [keepAspect, setKeepAspect] = useState(true);
  const [smoothing, setSmoothing] = useState<'smooth' | 'pixel'>('smooth');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sourceUrlRef = useRef<string | null>(null);
  const downloadUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
      if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current);
    };
  }, []);

  function handleFile(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLoadError('Please choose an image file.');
      return;
    }
    setLoadError(null);
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    const url = URL.createObjectURL(file);
    sourceUrlRef.current = url;
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setTargetWidth(img.naturalWidth * 2);
      setTargetHeight(img.naturalHeight * 2);
    };
    img.onerror = () => setLoadError('That image could not be loaded.');
    img.src = url;
  }

  const outSize = useMemo(() => {
    if (!image) return null;
    return computeEnlargedSize(
      image.naturalWidth,
      image.naturalHeight,
      mode,
      scale,
      targetWidth,
      targetHeight,
      keepAspect,
    );
  }, [image, mode, scale, targetWidth, targetHeight, keepAspect]);

  const tooLarge = outSize ? outSize.width * outSize.height > MAX_PIXELS : false;
  const error =
    loadError ?? (tooLarge ? 'That output is too large. Try a smaller scale or dimensions.' : null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!image || !canvasRef.current || !outSize || tooLarge) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = outSize;

    canvas.width = size.width;
    canvas.height = size.height;
    ctx.imageSmoothingEnabled = smoothing === 'smooth';
    if (smoothing === 'smooth') ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, size.width, size.height);
    ctx.drawImage(image, 0, 0, size.width, size.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current);
      const url = URL.createObjectURL(blob);
      downloadUrlRef.current = url;
      setDownloadUrl(url);
    }, 'image/png');
  }, [image, outSize, tooLarge, smoothing]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Image enlarger</span>
        <h2 className="tool-live-title">Enlarge and upscale an image</h2>
        <p className="tool-live-subtitle">
          Upload an image and resample it up by 2x, 3x, 4x, or to exact dimensions. This is honest
          canvas resampling, not AI super-resolution, so it will not invent new detail. Runs in your
          browser. Nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}

          {image ? (
            <>
              <label className="tool-live-field tool-live-field-full">
                <span>Mode</span>
                <select
                  onChange={(event) => setMode(event.target.value as 'scale' | 'dimensions')}
                  value={mode}
                >
                  <option value="scale">Scale by factor</option>
                  <option value="dimensions">Target dimensions</option>
                </select>
              </label>

              {mode === 'scale' ? (
                <label className="tool-live-field tool-live-field-full">
                  <span>Scale factor</span>
                  <select onChange={(event) => setScale(Number(event.target.value))} value={scale}>
                    <option value={2}>2x</option>
                    <option value={3}>3x</option>
                    <option value={4}>4x</option>
                  </select>
                </label>
              ) : (
                <>
                  <label className="tool-live-field">
                    <span>Width (px)</span>
                    <input
                      min={1}
                      onChange={(event) => setTargetWidth(Number(event.target.value))}
                      type="number"
                      value={targetWidth}
                    />
                  </label>
                  <label className="tool-live-field">
                    <span>Height (px)</span>
                    <input
                      disabled={keepAspect}
                      min={1}
                      onChange={(event) => setTargetHeight(Number(event.target.value))}
                      type="number"
                      value={targetHeight}
                    />
                  </label>
                  <label
                    className="tool-live-field tool-live-field-full"
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                  >
                    <input
                      checked={keepAspect}
                      onChange={(event) => setKeepAspect(event.target.checked)}
                      type="checkbox"
                    />
                    <span>Keep aspect ratio</span>
                  </label>
                </>
              )}

              <label className="tool-live-field tool-live-field-full">
                <span>Resampling</span>
                <select
                  onChange={(event) => setSmoothing(event.target.value as 'smooth' | 'pixel')}
                  value={smoothing}
                >
                  <option value="smooth">Smooth / bilinear (photos)</option>
                  <option value="pixel">Nearest-neighbor (pixel art)</option>
                </select>
              </label>

              <div className="tool-live-actions">
                {downloadUrl && !error ? (
                  <a className="button button-secondary" download="enlarged.png" href={downloadUrl}>
                    Download PNG
                  </a>
                ) : null}
              </div>

              {outSize ? (
                <p className="tool-live-panel-note">
                  Source {image.naturalWidth}×{image.naturalHeight} → output {outSize.width}×
                  {outSize.height} px.
                </p>
              ) : null}
            </>
          ) : (
            <p className="tool-live-panel-note">Pick an image to enlarge it.</p>
          )}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image && !error ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
